/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, FormControl, RadioGroup, FormControlLabel, Radio, Select, MenuItem, IconButton, Typography, Box, Checkbox, ListItemText, OutlinedInput } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { styled } from '@mui/system';
import { createPersonaAPI, editPersonaAPI } from 'Samarth Module/api/personaAPI';
import { getAdminDetails } from "Samarth Module/api/roleBaseApi";

const StyledDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialog-paper': {
        borderRadius: '8px',
        minWidth: '600px',
        maxWidth: '800px',
    }
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
    backgroundColor: '#2036C7',
    color: '#fff',
    fontSize: '16px',
    fontWeight: 600,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 20px',
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
    '& .MuiOutlinedInput-root': {
        fontSize: '13px',
        height: '36px',
    },
    '& .MuiOutlinedInput-input': {
        padding: '8px 12px',
    },
    '& .MuiInputLabel-root': {
        fontSize: '13px',
    }
}));

const StyledSelect = styled(Select)(({ theme }) => ({
    fontSize: '13px',
    height: '36px',
    '& .MuiSelect-select': {
        padding: '8px 12px',
        display: 'flex',
        alignItems: 'center',
    },
    '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#ddd',
    }
}));

const ITEM_HEIGHT = 36;
const ITEM_PADDING_TOP = 4;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250,
        },
    },
};

const AddAndEditPopup = ({ open, onClose, onSubmit, type, selectedPersonaData }) => {
    const [formData, setFormData] = useState({
        persona: '',
        scope: '',
        pages: [],
        accessTypes: {}
    });

    // Available options
    const scopeOptions = ['All Access', 'BG', 'Site Cluster'];
    const pageOptions = [
        'Agile OR',
        'Cognitive Contract Coverage',
        'Daily Phasing insight',
        'DPR(Plan Vs Actual)',
        'Landing Page',
        'Manpower Shortage',
        'OR Analytics',
        'Prioritization Matrix',
        'RMPM Analytics',
        'RMPM Autonomous Engine',
        'Smart Production Scheduler',
        'Supplier Collaboration Agent',
        'Disptach Priority',
        'PO Priority',
        'Hands Free VMI',
        'Order To Delivery Hub',
        'Loss To Action Engine',
        'RMPM Summary',
        'NR Replenishment Engine'

    ];

    useEffect(() => {
        if (type === 'edit' && selectedPersonaData) {
            // Check if selectedPersonaData is an array (multiple entries for same persona)
            if (Array.isArray(selectedPersonaData) && selectedPersonaData.length > 0) {
                // Extract unique values from the array
                const firstEntry = selectedPersonaData[0];
                const allPages = selectedPersonaData.map(item => item.Page);
                const accessTypesMap = {};

                // Build access types mapping
                selectedPersonaData.forEach(item => {
                    accessTypesMap[item.Page] = item['Access Type'];
                });

                setFormData({
                    persona: firstEntry.Persona || '',
                    scope: firstEntry.Scope || '',
                    pages: allPages,
                    accessTypes: accessTypesMap
                });
            } else {
                // Handle single object (backward compatibility)
                setFormData({
                    persona: selectedPersonaData.Persona || '',
                    scope: selectedPersonaData.Scope || '',
                    pages: selectedPersonaData.Page ? [selectedPersonaData.Page] : [],
                    accessTypes: {
                        [selectedPersonaData.Page]: selectedPersonaData['Access Type']
                    }
                });
            }
        } else {
            // Reset form for add mode
            setFormData({
                persona: '',
                scope: '',
                pages: [],
                accessTypes: {}
            });
        }
    }, [type, selectedPersonaData, open]);

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handlePageSelectionChange = (event) => {
        const {
            target: { value },
        } = event;

        const selectedPages = typeof value === 'string' ? value.split(',') : value;

        setFormData(prev => {
            const newAccessTypes = { ...prev.accessTypes };
            prev.pages.forEach(page => {
                if (!selectedPages.includes(page)) {
                    delete newAccessTypes[page];
                }
            });
            selectedPages.forEach(page => {
                if (!prev.pages.includes(page)) {
                    newAccessTypes[page] = 'Read';
                }
            });

            return {
                ...prev,
                pages: selectedPages,
                accessTypes: newAccessTypes
            };
        });
    };

    const handleAccessTypeChange = (page, accessType) => {
        setFormData(prev => ({
            ...prev,
            accessTypes: {
                ...prev.accessTypes,
                [page]: accessType
            }
        }));
    };

    const handleSubmit = async () => {
        if (!formData.persona || !formData.scope || formData.pages.length === 0) {
            return;
        }

        const submissionData = {
            persona: formData.persona,
            scope: formData.scope,
            pages: formData.pages.map(page => {
                // Find the ID for this page if in edit mode
                let pageId = null;
                if (type === 'edit' && Array.isArray(selectedPersonaData)) {
                    const existingPage = selectedPersonaData.find(item => item.Page === page);
                    pageId = existingPage?.ID || null;
                }

                return {
                    ...(pageId && { id: pageId }), // Include ID only if it exists (edit mode)
                    page,
                    accessType: formData.accessTypes[page] || 'Read'
                };
            })
        };
        try {
            let response;

            if (type === 'edit') {
                response = await editPersonaAPI(submissionData);
            } else {
                response = await createPersonaAPI(submissionData);
            }
            onSubmit?.(response.data);
            handleClose();
            const adminResponse = await getAdminDetails();
            
            if (adminResponse?.data) {
                localStorage.setItem(
                    "UserInfo",
                    JSON.stringify(adminResponse.data)
                );
            }
        } catch (error) {
            console.error(`Error ${type === 'edit' ? 'updating' : 'creating'} persona:`, error);
        }
    };

    const handleClose = () => {
        setFormData({
            persona: '',
            scope: '',
            pages: [],
            accessTypes: {}
        });
        onClose();
    };

    return (
        <StyledDialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <StyledDialogTitle>
                <Typography variant="h6" component="div" sx={{ fontSize: '16px' }}>
                    {type === 'add' ? 'Add New' : 'Edit'} Persona
                </Typography>
                <IconButton
                    edge="end"
                    color="inherit"
                    onClick={handleClose}
                    size="small"
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
            </StyledDialogTitle>

            <DialogContent sx={{ padding: '16px 20px' }}>
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600, fontSize: '1rem' }}>
                            Persona
                        </Typography>
                        <StyledTextField
                            fullWidth
                            placeholder="Enter Persona Name"
                            value={formData.persona}
                            onChange={(e) => handleInputChange('persona', e.target.value)}
                            variant="outlined"
                            size="small"
                            disabled={type === 'edit'}
                        />
                    </Box>

                    <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600, fontSize: '13px' }}>
                            Scope
                        </Typography>
                        <FormControl fullWidth size="small">
                            <StyledSelect
                                value={formData.scope}
                                onChange={(e) => handleInputChange('scope', e.target.value)}
                                displayEmpty
                            >
                                <MenuItem value="" disabled>Select</MenuItem>
                                {scopeOptions.map(option => (
                                    <MenuItem key={option} value={option} sx={{ fontSize: '13px', padding: '6px 12px' }}>
                                        {option}
                                    </MenuItem>
                                ))}
                            </StyledSelect>
                        </FormControl>
                    </Box>
                </Box>

                <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, fontSize: '13px' }}>
                        Pages & Access Types
                    </Typography>

                    <Box sx={{ mb: 1.5 }}>
                        <FormControl fullWidth size="small">
                            <StyledSelect
                                multiple
                                value={formData.pages}
                                onChange={handlePageSelectionChange}
                                input={<OutlinedInput sx={{ height: '36px' }} />}
                                renderValue={(selected) =>
                                    selected.length === 0
                                        ? <em style={{ color: '#999', fontSize: '13px' }}>Select Pages</em>
                                        : (
                                            <span style={{
                                                fontSize: '13px',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                display: 'block'
                                            }}>
                                                {selected.join(', ')}
                                            </span>
                                        )
                                }
                                MenuProps={MenuProps}
                                displayEmpty
                            >
                                <MenuItem value="" disabled sx={{ fontSize: '13px', padding: '6px 12px' }}>
                                    <em>Select Pages</em>
                                </MenuItem>
                                {pageOptions.map((page) => (
                                    <MenuItem key={page} value={page} sx={{ fontSize: '13px', padding: '4px 12px' }}>
                                        <Checkbox
                                            checked={formData.pages.includes(page)}
                                            size="small"
                                            sx={{ padding: '2px' }}
                                        />
                                        <ListItemText
                                            primary={page}
                                            sx={{
                                                '& .MuiListItemText-primary': {
                                                    fontSize: '13px',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap'
                                                },
                                                margin: 0
                                            }}
                                        />
                                    </MenuItem>
                                ))}
                            </StyledSelect>
                        </FormControl>
                    </Box>

                    {formData.pages.length > 0 && (
                        <Box>
                            <Typography variant="body2" sx={{ mb: 0.5, color: '#666', fontSize: '12px' }}>
                                Set Access Types:
                            </Typography>
                            {formData.pages.map(page => (
                                <Box key={page} sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    mb: 0.5,
                                    p: '6px 8px',
                                    bgcolor: '#f8f8f8',
                                    borderRadius: '4px',
                                    minHeight: '32px'
                                }}>
                                    <Typography variant="body2" sx={{ minWidth: '180px', fontSize: '13px' }}>
                                        {page}
                                    </Typography>
                                    <FormControl component="fieldset">
                                        <RadioGroup
                                            row
                                            value={formData.accessTypes[page] || 'Read'}
                                            onChange={(e) => handleAccessTypeChange(page, e.target.value)}
                                            sx={{ gap: 1 }}
                                        >
                                            <FormControlLabel
                                                value="Read"
                                                control={<Radio size="small" sx={{ padding: '2px' }} />}
                                                label="Read"
                                                sx={{
                                                    margin: 0,
                                                    '& .MuiFormControlLabel-label': {
                                                        fontSize: '12px'
                                                    }
                                                }}
                                            />
                                            <FormControlLabel
                                                value="Read-Write"
                                                control={<Radio size="small" sx={{ padding: '2px' }} />}
                                                label="Read-Write"
                                                sx={{
                                                    margin: 0,
                                                    '& .MuiFormControlLabel-label': {
                                                        fontSize: '12px'
                                                    }
                                                }}
                                            />
                                        </RadioGroup>
                                    </FormControl>
                                </Box>
                            ))}
                        </Box>
                    )}
                </Box>
            </DialogContent>

            <DialogActions sx={{ padding: '12px 20px', gap: 1.5 }}>
                <Button
                    onClick={handleClose}
                    variant="outlined"
                    size="small"
                    sx={{
                        color: '#666',
                        borderColor: '#ddd',
                        fontSize: '13px',
                        padding: '6px 16px',
                        '&:hover': { borderColor: '#999' }
                    }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    size="small"
                    sx={{
                        backgroundColor: '#2036C7',
                        fontSize: '13px',
                        padding: '6px 16px',
                        '&:hover': { backgroundColor: '#1c2ea3' }
                    }}
                    disabled={!formData.persona || !formData.scope || formData.pages.length === 0}
                >
                    Save
                </Button>
            </DialogActions>
        </StyledDialog>
    );
};

export default AddAndEditPopup;

AddAndEditPopup.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func,
    onSubmit: PropTypes.func,
    type: PropTypes.oneOf(['add', 'edit']),
    selectedPersonaData: PropTypes.oneOfType([
        PropTypes.array,
        PropTypes.object,
    ]),
};